import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Heart,
  PackageCheck,
  ShoppingCart,
  Star,
  Truck,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import { useCart } from "../../../context/CartContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  fetchCategoryBrowse,
  resetBrowse,
} from "../../../Redux/ecomCatalog/slice";
import type { EcomBrowseQueryParams } from "../../../Redux/ecomCatalog/types";
import {
  formatPrice,
  mapCatalogProductToCard,
  slugify,
  type CategoryCardData,
  type MobileCategoryFilterSection,
  mobileCategoryFilterSections,
} from "./CategoryListingTypes";
import "./CategoryListingPage.css";
import CategoryShowcaseSection from "./CategoryShowcaseSection/CategoryShowcaseSection";
import CategoryMobileFilterRail from "./components/CategoryMobileFilterRail";
import CategoryMobileFilterSheet from "./components/CategoryMobileFilterSheet";

const PAGE_SIZE = 24;

const CategoryListingPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { addToCart, isProductInCart, cartCount } = useCart();
  const dispatch = useAppDispatch();
  const { browse, browseLoading, loadingMore, browseError } = useAppSelector(
    (s) => s.ecomCatalog,
  );

  // ─── Route → API params ────────────────────────────────────────────────────
  // Two route shapes are mounted to this page:
  //   1. /category/men-fashion/t-shirts/round-neck/slim-fit  (wildcard)
  //   2. /Categories/:superCategory?/:parentCategory?/:subCategory?/:childCategory?
  // The HomeNavbar search bar navigates to the second shape with
  // superCategory="All" and ?q={query} so we read q from useSearchParams.
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") ?? "";

  const wildcardPath = params["*"] ?? "";
  const segments = useMemo(() => {
    // Route shape 1: /category/* (wildcard) — segments are in params["*"]
    if (wildcardPath) {
      return wildcardPath.split("/").filter(Boolean);
    }
    // Route shape 2: /Categories/:superCategory?/:parentCategory?/:subCategory?/:childCategory?
    const named = [
      params.superCategory,
      params.parentCategory,
      params.subCategory,
      params.childCategory,
    ].filter(Boolean) as string[];
    return named;
  }, [wildcardPath, params.superCategory, params.parentCategory, params.subCategory, params.childCategory]);

  // "All" superCategory (from search) means no category filter — search across everything.
  const rootSegment =
    segments[0] && segments[0].toLowerCase() !== "all" ? segments[0] : "";

  // ─── Filter / sort state (server-side) ──────────────────────────────────────
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<number[]>([]);
  const [selectedDiscounts, setSelectedDiscounts] = useState<number[]>([]);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [fastDelivery, setFastDelivery] = useState(false);
  const [nextDayDelivery, setNextDayDelivery] = useState(false);
  const [sort, setSort] = useState<string>("newest");

  // Per-card UI state (local, not server)
  const [wishlist, setWishlist] = useState<Record<string, boolean>>({});
  const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);

  // Mobile filter drawer state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileFilterSection, setMobileFilterSection] =
    useState<MobileCategoryFilterSection>("sort");

  useEffect(() => {
    if (!mobileFilterOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFilterOpen]);

  const openMobileFilter = (section: MobileCategoryFilterSection) => {
    setMobileFilterSection(section);
    setMobileFilterOpen(true);
  };

  const clearAllMobileFilters = () => {
    setSelectedBrands([]);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setSelectedColors([]);
    setSelectedMaterials([]);
    setSelectedRatings([]);
    setSelectedDiscounts([]);
    setFreeDelivery(false);
    setFastDelivery(false);
    setNextDayDelivery(false);
    setSort("newest");
  };

  const getMobileFilterCount = (section: MobileCategoryFilterSection): number => {
    if (section === "sort") {
      return sort === "newest" ? 0 : 1;
    }
    if (section === "price") {
      return minPrice !== undefined || maxPrice !== undefined ? 1 : 0;
    }
    if (section === "delivery") {
      return (
        (freeDelivery ? 1 : 0) +
        (fastDelivery ? 1 : 0) +
        (nextDayDelivery ? 1 : 0)
      );
    }
    if (section === "brands") {
      return selectedBrands.length;
    }
    if (section === "ratings") {
      return selectedRatings.length;
    }
    if (section === "deals") {
      return selectedDiscounts.length;
    }
    if (section === "colors") {
      return selectedColors.length;
    }
    if (section === "materials") {
      return selectedMaterials.length;
    }
    if (section === "discounts") {
      return selectedDiscounts.length;
    }
    return 0;
  };

  const activeMobileFilterCount = mobileCategoryFilterSections.reduce(
    (total, sec) => total + getMobileFilterCount(sec.id),
    0
  );

  // ─── Build query params from route + filters + sort + search ────────────────
  const queryParams: EcomBrowseQueryParams = useMemo(() => {
    const q: EcomBrowseQueryParams = {
      limit: PAGE_SIZE,
      sort,
      withFilters: "true",
    };
    // Search query (from HomeNavbar search bar) — applies across all categories
    if (searchQuery) q.q = searchQuery;
    // rootSegment is already "" for "All" superCategory → no superCategory filter
    if (segments[0] && segments[0].toLowerCase() !== "all") q.superCategory = segments[0];
    if (segments[1]) q.category = segments[1];
    if (segments[2]) q.subCategory = segments[2];
    if (segments[3]) q.childCategory = segments[3];
    if (selectedBrands.length) q.brandId = selectedBrands;
    if (minPrice !== undefined) q.minPrice = minPrice;
    if (maxPrice !== undefined) q.maxPrice = maxPrice;
    if (selectedColors.length || selectedMaterials.length) {
      q.attributes = {};
      if (selectedColors.length) q.attributes["Color"] = selectedColors;
      if (selectedMaterials.length) q.attributes["Material"] = selectedMaterials;
    }
    if (freeDelivery) q.freeDelivery = "true";
    if (fastDelivery) q.fastDelivery = "true";
    if (nextDayDelivery) q.nextDayDelivery = "true";
    if (selectedRatings.length) q.minRating = Math.min(...selectedRatings);
    if (selectedDiscounts.length) q.minDiscount = Math.min(...selectedDiscounts);
    return q;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    segments.join("/"),
    searchQuery,
    selectedBrands,
    minPrice,
    maxPrice,
    selectedColors,
    selectedMaterials,
    freeDelivery,
    fastDelivery,
    nextDayDelivery,
    selectedRatings,
    selectedDiscounts,
    sort,
  ]);

  // ─── Reset filters when navigating to a different category root or new search ──
  useEffect(() => {
    setSelectedBrands([]);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setSelectedColors([]);
    setSelectedMaterials([]);
    setSelectedRatings([]);
    setSelectedDiscounts([]);
    setFreeDelivery(false);
    setFastDelivery(false);
    setNextDayDelivery(false);
    setSort("newest");
  }, [rootSegment, searchQuery]);

  // ─── Fetch browse data on route / filter / sort change ──────────────────────
  // Re-fetch on every change of the resolved query params.
  useEffect(() => {
    dispatch(resetBrowse());
    dispatch(fetchCategoryBrowse(queryParams));
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [dispatch, queryParams]);

  // ─── Infinite scroll — load more when sentinel enters viewport ──────────────
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nextCursor = (browse.pagination as any)?.nextCursor;
    const hasMore = (browse.pagination as any)?.hasMore;
    if (!nextCursor || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          dispatch(
            fetchCategoryBrowse({
              ...queryParams,
              cursor: nextCursor,
              withFilters: "false", // skip facet recompute on load-more
            }),
          );
        }
      },
      { rootMargin: "300px" },
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [
    dispatch,
    queryParams,
    (browse.pagination as any)?.nextCursor,
    (browse.pagination as any)?.hasMore,
    loadingMore,
  ]);

  // ─── Map catalog products → UI cards ───────────────────────────────────────
  const cards: CategoryCardData[] = useMemo(
    () => browse.products.map((p) => mapCatalogProductToCard(p)),
    [browse.products],
  );

  // ─── Derived labels ─────────────────────────────────────────────────────────
  const pageTitle = useMemo(() => {
    // Search results take priority in the heading — same convention as Categories.tsx
    if (searchQuery) return `Search: "${searchQuery}"`;
    if (browse.breadcrumb.length > 0) {
      return browse.breadcrumb[browse.breadcrumb.length - 1].name;
    }
    return rootSegment ? rootSegment.replace(/-/g, " ") : "All Products";
  }, [browse.breadcrumb, rootSegment, searchQuery]);

  const resultLabel = useMemo(() => {
    if (searchQuery) return `"${searchQuery}"`;
    if (browse.breadcrumb.length > 0) {
      return browse.breadcrumb.map((c) => c.name).join(" › ");
    }
    return pageTitle;
  }, [browse.breadcrumb, pageTitle, searchQuery]);

  // ─── Filter handlers ─────────────────────────────────────────────────────────
  const toggleBrand = (brandId: string) =>
    setSelectedBrands((prev) =>
      prev.includes(brandId)
        ? prev.filter((b) => b !== brandId)
        : [...prev, brandId],
    );

  const toggleColor = (color: string) =>
    setSelectedColors((prev) =>
      prev.includes(color)
        ? prev.filter((c) => c !== color)
        : [...prev, color],
    );

  const toggleMaterial = (material: string) =>
    setSelectedMaterials((prev) =>
      prev.includes(material)
        ? prev.filter((m) => m !== material)
        : [...prev, material],
    );

  const toggleRating = (rating: number) =>
    setSelectedRatings((prev) =>
      prev.includes(rating)
        ? prev.filter((r) => r !== rating)
        : [...prev, rating],
    );

  const toggleDiscount = (discount: number) =>
    setSelectedDiscounts((prev) =>
      prev.includes(discount)
        ? prev.filter((d) => d !== discount)
        : [...prev, discount],
    );

  const toggleWishlist = (id: string) =>
    setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const buildCategoryUrl = (pathSegments: string[]) => {
    const suffix = pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "";
    const root = rootSegment || "";
    return `/category/${root}${suffix}`;
  };

  const handleShoppingIdeaClick = (childName: string) => {
    const newSegment = slugify(childName);
    const currentDepth = segments.length;
    const nextSegments = [
      ...segments.slice(1), // drop root from segments (buildCategoryUrl re-prepends it)
      ...segments.slice(1, currentDepth - 1), // keep intermediate path
      newSegment,
    ];
    // Simpler: append the new child to the current path (children are one level deeper)
    navigate(`/category/${[...segments, newSegment].join("/")}`);
  };

  const handleShoppingBack = () => {
    navigate(`/category/${segments.slice(0, -1).join("/")}`);
  };

  const handleBreadcrumbClick = (index: number) => {
    // breadcrumb level values are names; map to slugified path
    const path = segments.slice(0, index + 1);
    navigate(`/category/${path.join("/")}`);
  };

  const handleProductClick = (card: CategoryCardData) => {
    navigate(`/DetailPage/${card.id}`);
  };

  const handleAddToCart = (card: CategoryCardData) => {
    if (isProductInCart(card.id)) return;
    addToCart({
      productId: card.id,
      title: card.name,
      image: card.image,
      price: card.price,
      mrp: card.mrp,
      color: card.colors[0],
    });
  };

  // ─── Facet data from API ────────────────────────────────────────────────────
  const filters = browse.filters;
  const priceRange = filters?.priceRange;
  const colorOptions =
    (filters?.attributes?.Color as any as { value: string; count: number; selected: boolean }[]) ??
    [];
  const materialOptions =
    (filters?.attributes?.Material as any as { value: string; count: number; selected: boolean }[]) ??
    [];

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="category-listing-desktop-navbar">
        <HomeNavbar variant="home" />
      </div>

      <div className="category-listing-mobile-navbar">
        <MobileDetailNavbar
          cartCount={cartCount}
          searchPlaceholder={`Search ${pageTitle}`}
          fallbackPath="/HomePage"
        />
      </div>

      <div className="category-listing-desktop-secondary-navbar">
        <HomeSecondaryNavbar
          variant="detail"
          browsingHistoryOpen={browsingHistoryOpen}
          onBrowsingHistoryClick={() =>
            setBrowsingHistoryOpen((prev) => !prev)
          }
        />
      </div>

      <CategoryMobileFilterRail
        activeMobileFilterCount={activeMobileFilterCount}
        getMobileFilterCount={getMobileFilterCount}
        onOpenMobileFilter={openMobileFilter}
      />

      <main className="category-listing-page">
        <div className="category-listing-container">
          {/* ─── Sidebar: navigation + filters ─────────────────────────────── */}
          <aside className="category-listing-sidebar">
            {/* Popular Shopping Ideas — live children from API */}
            <section className="category-sidebar-section category-shopping-section">
              <div className="category-shopping-title">
                {segments.length > 0 && (
                  <button
                    type="button"
                    className="category-shopping-back"
                    onClick={handleShoppingBack}
                    aria-label="Back to previous category"
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                  </button>
                )}
                <h3>Popular Shopping Ideas</h3>
              </div>
              <div className="category-sidebar-links category-shopping-links">
                {browse.children.map((child) => (
                  <button
                    type="button"
                    key={child.name}
                    className="category-shopping-link"
                    onClick={() => handleShoppingIdeaClick(child.name)}
                  >
                    <span>{child.name}</span>
                    <strong className="category-shopping-count">
                      {child.count}
                    </strong>
                    <ChevronRight size={14} strokeWidth={1.8} />
                  </button>
                ))}
              </div>
            </section>

            {/* Delivery Day */}
            <section className="category-sidebar-section">
              <h3>Delivery Day</h3>
              {[
                { label: "Free Delivery", value: freeDelivery, set: setFreeDelivery },
                { label: "Fast Delivery", value: fastDelivery, set: setFastDelivery },
                { label: "Next Day Delivery", value: nextDayDelivery, set: setNextDayDelivery },
              ].map((opt) => (
                <label className="category-filter-option" key={opt.label}>
                  <input
                    type="checkbox"
                    checked={opt.value}
                    onChange={() => opt.set(!opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </section>

            {/* Brands — from API facet */}
            {filters && filters.brands.length > 0 && (
              <section className="category-sidebar-section">
                <h3>Brands</h3>
                {filters.brands.map((brand) => (
                  <label className="category-filter-option" key={brand._id}>
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand._id)}
                      onChange={() => toggleBrand(brand._id)}
                    />
                    <span>
                      {brand.name} ({brand.count})
                    </span>
                  </label>
                ))}
              </section>
            )}

            {/* Price — from API facet */}
            {priceRange && priceRange.max > priceRange.min && (
              <section className="category-sidebar-section">
                <h3>Price</h3>
                <div className="category-price-range" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      type="number"
                      placeholder={`₹${priceRange.min}`}
                      value={minPrice ?? ""}
                      onChange={(e) =>
                        setMinPrice(e.target.value ? Number(e.target.value) : undefined)
                      }
                      style={{ width: "100%" }}
                    />
                    <input
                      type="number"
                      placeholder={`₹${priceRange.max}`}
                      value={maxPrice ?? ""}
                      onChange={(e) =>
                        setMaxPrice(e.target.value ? Number(e.target.value) : undefined)
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                  {(minPrice !== undefined || maxPrice !== undefined) && (
                    <button
                      type="button"
                      onClick={() => {
                        setMinPrice(undefined);
                        setMaxPrice(undefined);
                      }}
                      style={{ alignSelf: "flex-start" }}
                    >
                      Clear price
                    </button>
                  )}
                </div>
              </section>
            )}

            {/* Customer Reviews — from API rating buckets */}
            {filters && filters.ratingBuckets.length > 0 && (
              <section className="category-sidebar-section">
                <h3>Customer Reviews</h3>
                {filters.ratingBuckets.map((bucket) => (
                  <label className="category-review-option" key={bucket.minRating}>
                    <input
                      type="checkbox"
                      checked={selectedRatings.includes(bucket.minRating)}
                      onChange={() => toggleRating(bucket.minRating)}
                    />
                    <span className="category-review-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={14}
                          strokeWidth={1.8}
                          fill={star <= bucket.minRating ? "currentColor" : "none"}
                        />
                      ))}
                    </span>
                    <strong>& Up ({bucket.count})</strong>
                  </label>
                ))}
              </section>
            )}

            {/* Deals & Discounts — from API discount buckets */}
            {filters && filters.discountBuckets.length > 0 && (
              <section className="category-sidebar-section">
                <h3>Deals & Discounts</h3>
                {filters.discountBuckets.map((bucket) => (
                  <label className="category-filter-option" key={bucket.minDiscount}>
                    <input
                      type="checkbox"
                      checked={selectedDiscounts.includes(bucket.minDiscount)}
                      onChange={() => toggleDiscount(bucket.minDiscount)}
                    />
                    <span>
                      {bucket.label} ({bucket.count})
                    </span>
                  </label>
                ))}
              </section>
            )}

            {/* Colors — from API attributes.Color */}
            {colorOptions.length > 0 && (
              <section className="category-sidebar-section">
                <h3>Colors</h3>
                <div className="category-color-filter-list">
                  {colorOptions.map((color) => (
                    <label className="category-color-filter-option" key={color.value}>
                      <input
                        type="checkbox"
                        checked={selectedColors.includes(color.value)}
                        onChange={() => toggleColor(color.value)}
                      />
                      <span
                        className="category-color-filter-dot"
                        style={{ backgroundColor: color.value }}
                      />
                      <strong>{color.value} ({color.count})</strong>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Material Type — from API attributes.Material */}
            {materialOptions.length > 0 && (
              <section className="category-sidebar-section">
                <h3>Material Type</h3>
                {materialOptions.map((mat) => (
                  <label className="category-filter-option" key={mat.value}>
                    <input
                      type="checkbox"
                      checked={selectedMaterials.includes(mat.value)}
                      onChange={() => toggleMaterial(mat.value)}
                    />
                    <span>{mat.value} ({mat.count})</span>
                  </label>
                ))}
              </section>
            )}
          </aside>

          {/* ─── Main content ───────────────────────────────────────────────── */}
          <section className="category-listing-content">
            {/* Showcase section — hidden on search results (no category context) */}
            {!searchQuery && <CategoryShowcaseSection />}

            {/* Breadcrumbs */}
            <div className="category-listing-breadcrumbs">
              <button type="button" onClick={() => navigate("/HomePage")}>
                Home
              </button>
              <ChevronRight size={11} />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/Categories/All`, { replace: true })
                  }
                >
                  Search
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      rootSegment
                        ? `/category/${rootSegment}`
                        : "/Categories/All",
                    )
                  }
                >
                  {rootSegment ? rootSegment.replace(/-/g, " ") : "All"}
                </button>
              )}
              {!searchQuery &&
                browse.breadcrumb.slice(1).map((crumb, index) => (
                  <span className="category-breadcrumb-item" key={`${crumb.level}-${index}`}>
                    <ChevronRight size={11} />
                    <button type="button" onClick={() => handleBreadcrumbClick(index + 1)}>
                      {crumb.name}
                    </button>
                  </span>
                ))}
              {searchQuery && (
                <span className="category-breadcrumb-item">
                  <ChevronRight size={11} />
                  <button type="button" disabled>
                    {searchQuery}
                  </button>
                </span>
              )}
            </div>

            {/* Heading */}
            <div className="category-listing-heading">
              <div>
                <span>{searchQuery ? "SEARCH RESULTS" : "CURATED COLLECTION"}</span>
                <h1>{pageTitle}</h1>
                <p>
                  {searchQuery
                    ? "Products matching your search across all categories."
                    : "Premium fits, trending styles and everyday essentials selected for you."}
                </p>
              </div>
              <strong>{cards.length} products</strong>
            </div>

            {/* Toolbar */}
            <div className="category-listing-toolbar">
              <div>
                Showing <strong>{cards.length}</strong> results for{" "}
                <strong>“{resultLabel}”</strong>
              </div>
              <div className="category-listing-sort">
                <span>Sort by</span>
                <div>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                  >
                    <option value="newest">Featured</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="rating">Customer Rating</option>
                    <option value="discount">Discount</option>
                  </select>
                  <ChevronDown size={14} />
                </div>
              </div>
            </div>

            {/* Product grid */}
            <div className="category-results-stage">
              {/* Error state with retry */}
              {browseError && cards.length === 0 ? (
                <div className="category-listing-empty">
                  <h2>Something went wrong</h2>
                  <p>{browseError}</p>
                  <button
                    type="button"
                    onClick={() => dispatch(fetchCategoryBrowse(queryParams))}
                  >
                    Try again
                  </button>
                </div>
              ) : /* Initial loading skeleton */ browseLoading && cards.length === 0 ? (
                <div className="category-listing-grid">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="men-product-card"
                      style={{
                        background: "#f0f3f7",
                        animation: "categoryPulse 1.2s ease-in-out infinite",
                      }}
                    />
                  ))}
                </div>
              ) : /* Empty state */ cards.length === 0 ? (
                <div className="category-listing-empty">
                  <h2>No products found</h2>
                  <p>Try changing the selected filters.</p>
                </div>
              ) : (
                <div className="category-listing-grid">
                  {cards.map((card) => {
                    const isWishlisted = Boolean(wishlist[card.id]);
                    const addedToCart = isProductInCart(card.id);

                    return (
                      <article
                        className="men-product-card"
                        key={card.id}
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          const target = event.target as HTMLElement;
                          if (target.closest("button")) return;
                          handleProductClick(card);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleProductClick(card);
                          }
                        }}
                      >
                        <div className="men-product-image-wrapper">
                          {card.isSponsored ? (
                            <span className="men-product-badge">SPONSORED</span>
                          ) : card.dealLabel ? (
                            <span className="men-product-badge">DEAL</span>
                          ) : null}

                          <button
                            type="button"
                            className={`men-product-wishlist ${isWishlisted ? "active" : ""}`}
                            onClick={() => toggleWishlist(card.id)}
                            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                          >
                            <Heart
                              size={18}
                              strokeWidth={2}
                              fill={isWishlisted ? "currentColor" : "none"}
                            />
                          </button>

                          <img
                            src={card.image}
                            alt={card.name}
                            className="men-product-image"
                            loading="lazy"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                "/assets/images/logos/salarlogo.png";
                            }}
                          />
                        </div>

                        <div className="men-product-info">
                          {card.colors.length > 0 && (
                            <div className="men-product-colors">
                              <div className="men-product-color-list">
                                {card.colors.slice(0, 5).map((color, index) => (
                                  <button
                                    type="button"
                                    key={`${color}-${index}`}
                                    className="men-product-color"
                                    title={color}
                                    aria-label={color}
                                  >
                                    <span
                                      style={{
                                        backgroundColor: color,
                                        display: "block",
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "inherit",
                                      }}
                                    />
                                  </button>
                                ))}
                                {card.colors.length > 5 && (
                                  <button
                                    type="button"
                                    className="men-product-more-colors"
                                  >
                                    +{card.colors.length - 5} more
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          <h3>{card.name}</h3>
                          <p className="men-product-description">
                            {card.description || card.brandName}
                          </p>

                          <div className="men-product-rating">
                            {card.rating > 0 ? (
                              <>
                                <strong>{card.rating.toFixed(1)}</strong>
                                <span className="men-product-stars">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      size={13}
                                      strokeWidth={1.8}
                                      fill={star <= Math.round(card.rating) ? "currentColor" : "none"}
                                    />
                                  ))}
                                </span>
                                <span className="men-product-review-count">
                                  ({card.reviewCount})
                                </span>
                              </>
                            ) : (
                              <span className="men-product-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={13}
                                    strokeWidth={1.8}
                                    fill="none"
                                  />
                                ))}
                              </span>
                            )}
                          </div>

                          {card.dealLabel && (
                            <div className="men-product-deal limited">
                              <span className="men-product-deal-dot" />
                              <strong>{card.dealLabel}</strong>
                            </div>
                          )}

                          {card.couponLabel && (
                            <div className="men-product-deal special">
                              <span className="men-product-deal-dot" />
                              <strong>Coupon: {card.couponLabel}</strong>
                            </div>
                          )}

                          <div className="men-product-pricing">
                            <div className="men-product-price-main">
                              <strong>
                                <sup>₹</sup>
                                {formatPrice(card.price)}
                              </strong>
                              {card.discountPercent > 0 && (
                                <span className="men-product-discount">
                                  {card.discountPercent}% OFF
                                </span>
                              )}
                            </div>
                            {card.mrp > card.price && (
                              <div className="men-product-price-secondary">
                                <span>MRP</span>
                                <del>₹{formatPrice(card.mrp)}</del>
                                <span className="men-product-saving">
                                  Save ₹{formatPrice(card.mrp - card.price)}
                                </span>
                              </div>
                            )}
                          </div>

                          <div
                            className={`men-product-delivery ${
                              card.fastDelivery ? "fast" : card.freeDelivery ? "delivery" : "stock"
                            }`}
                          >
                            {card.nextDayDelivery ? (
                              <Zap size={15} strokeWidth={2} />
                            ) : card.fastDelivery ? (
                              <Zap size={15} strokeWidth={2} />
                            ) : card.freeDelivery ? (
                              <Truck size={15} strokeWidth={1.9} />
                            ) : (
                              <PackageCheck size={15} strokeWidth={1.9} />
                            )}
                            <span>
                              {card.nextDayDelivery
                                ? "Next Day Delivery"
                                : card.fastDelivery
                                  ? "Fast Delivery Available"
                                  : card.freeDelivery
                                    ? "Free Delivery"
                                    : "In Stock"}
                            </span>
                          </div>

                          <button
                            type="button"
                            className={`men-product-options men-product-add-cart ${addedToCart ? "added" : ""}`}
                            onClick={() => handleAddToCart(card)}
                          >
                            {addedToCart ? (
                              <>
                                <Check size={16} strokeWidth={2.4} />
                                Added to cart
                              </>
                            ) : (
                              <>
                                <ShoppingCart size={16} strokeWidth={2} />
                                Add to cart
                              </>
                            )}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {/* Infinite scroll sentinel + loading indicator */}
              {(browse.pagination as any)?.hasMore && (
                <div
                  ref={sentinelRef}
                  style={{
                    height: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#687487",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {loadingMore ? "Loading more products…" : "Scroll for more"}
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      <CategoryMobileFilterSheet
        isOpen={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        activeSection={mobileFilterSection}
        onSelectSection={setMobileFilterSection}
        getMobileFilterCount={getMobileFilterCount}
        totalProductsCount={cards.length}
        onClearAll={clearAllMobileFilters}
        sort={sort}
        onSortChange={setSort}
        minPrice={minPrice}
        maxPrice={maxPrice}
        priceRange={priceRange}
        onSetPrice={(min, max) => {
          setMinPrice(min);
          setMaxPrice(max);
        }}
        freeDelivery={freeDelivery}
        fastDelivery={fastDelivery}
        nextDayDelivery={nextDayDelivery}
        deliveryCounts={{
          freeDelivery: filters?.delivery?.freeDelivery?.count,
          fastDelivery: filters?.delivery?.fastDelivery?.count,
          nextDayDelivery: filters?.delivery?.nextDayDelivery?.count,
        }}
        onToggleDelivery={(type) => {
          if (type === "free") setFreeDelivery((v) => !v);
          else if (type === "fast") setFastDelivery((v) => !v);
          else setNextDayDelivery((v) => !v);
        }}
        brands={filters?.brands ?? []}
        selectedBrands={selectedBrands}
        onToggleBrand={toggleBrand}
        ratingBuckets={filters?.ratingBuckets ?? []}
        selectedRatings={selectedRatings}
        onToggleRating={toggleRating}
        discountBuckets={filters?.discountBuckets ?? []}
        selectedDiscounts={selectedDiscounts}
        onToggleDiscount={toggleDiscount}
        colors={colorOptions}
        selectedColors={selectedColors}
        onToggleColor={toggleColor}
        materials={materialOptions}
        selectedMaterials={selectedMaterials}
        onToggleMaterial={toggleMaterial}
      />
    </>
  );
};

export default CategoryListingPage;
